/**
 * Auth state for the UI. Token *storage* lives in ./tokens; this is the
 * observable slice the app renders from.
 */
import { create } from 'zustand';
import { run } from '@kapture/sync-core';
import * as authApi from './api';
import type { AuthUser } from './api';
import {
  clearTokens,
  loadProfile,
  loadRefreshToken,
  saveProfile,
  setTokens,
} from './tokens';
import { NetworkError } from '@kapture/api-client';

export type AuthStatus = 'unknown' | 'anonymous' | 'authenticating' | 'authenticated';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  orgId: string | null;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Restore a session from the stored refresh token. Runs once at boot. */
  hydrate: () => Promise<void>;
}

/**
 * Rows seeded before first login carry `org_id = ''` (client migration v2
 * defaults it). The first successful login is when we learn the real value.
 */
async function backfillOrgId(orgId: string): Promise<void> {
  for (const table of ['sessions', 'captures', 'field_values'] as const) {
    await run(`UPDATE ${table} SET org_id = ? WHERE org_id = ''`, [orgId]);
  }
}

async function adopt(result: authApi.AuthResult): Promise<Partial<AuthState>> {
  await setTokens({
    accessToken: result.accessToken,
    refreshToken: result.refreshToken,
    accessExpiresIn: result.accessExpiresIn,
  });
  await saveProfile(result.user);
  await backfillOrgId(result.user.org_id);
  return {
    status: 'authenticated',
    user: result.user,
    orgId: result.user.org_id,
    error: null,
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'unknown',
  user: null,
  orgId: null,
  error: null,

  async login(email, password) {
    set({ status: 'authenticating', error: null });
    try {
      set(await adopt(await authApi.login(email, password)));
    } catch (err) {
      set({
        status: 'anonymous',
        user: null,
        orgId: null,
        error: err instanceof Error ? err.message : 'Sign-in failed',
      });
      throw err;
    }
  },

  async logout() {
    await clearTokens();
    set({ status: 'anonymous', user: null, orgId: null, error: null });
  },

  async hydrate() {
    const refreshToken = await loadRefreshToken();
    if (!refreshToken) {
      set({ status: 'anonymous' });
      return;
    }
    try {
      set(await adopt(await authApi.refresh(refreshToken)));
    } catch (err) {
      const cached = await loadProfile<AuthUser>();
      if (err instanceof NetworkError && cached) {
        // Booting offline. The refresh token is still good as far as we know,
        // so come up signed in and let the queue keep working — requests will
        // 401 until we can actually refresh, which the queue treats as
        // retryable rather than fatal.
        set({ status: 'authenticated', user: cached, orgId: cached.org_id, error: null });
        return;
      }
      // Rejected by the server: expired, revoked, or unknown. Nothing to
      // restore, and the stored credential is worthless — drop it.
      await clearTokens();
      set({ status: 'anonymous', user: null, orgId: null });
    }
  },
}));
